import React, { useCallback, useRef } from 'react';
import { DynamicCasePreview, DEFAULT_SHOWCASE } from './CasePreview';

/**
 * ══════════════════════════════════════════════════════════════════════
 * VİTRİN YERLEŞİM EDİTÖRÜ (yönetici)
 * ══════════════════════════════════════════════════════════════════════
 * Kasa görselinde sergilenen en nadir 3 eşyanın konum, boyut ve açısını
 * ayarlar. Konum sürükleyerek, ince ayar kaydırıcılarla yapılır.
 *
 * ⚠️ SÜRÜKLEME NEDEN `pointer` OLAYLARI: mouse+touch tek API'de birleşir;
 * ayrı `touchstart/touchmove` yazmak gerekmiyor ve mobilde de çalışır.
 *
 * ⚠️ NEDEN `setPointerCapture`: imleç önizleme kutusunun DIŞINA çıksa
 * bile olaylar bu öğeye gelmeye devam eder. Olmadan hızlı sürüklemede
 * eşya "düşüyor" ve kullanıcı bırakmadığı hâlde takip kesiliyor.
 *
 * ⚠️ PERFORMANS: sürükleme sırasında state saniyede onlarca kez
 * güncelleniyor. Bu yüzden:
 *   • Kapsayıcı ölçüsü sürükleme BAŞLARKEN bir kez okunur (`rectRef`);
 *     her harekette `getBoundingClientRect()` çağırmak layout'u zorlar
 *     (reflow) ve gözle görülür takılma yapar.
 *   • Yalnızca AKTİF slot güncellenir; diğer ikisi aynı referansta kalır.
 */

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const SLOT_LABELS = ['Rarest', '2nd', '3rd'];

/**
 * @param {boolean} simple  Sade mod: yalnızca SÜRÜKLE + BOYUT.
 *   ⚠️ Community kasalarında kullanılıyor. Sıradan kullanıcıya açı ve
 *   X/Y kaydırıcıları göstermek gereksiz karmaşa; konum zaten sürükleyerek
 *   ayarlanıyor. Yönetici panelinde tam mod (açı + hassas X/Y) açık kalır.
 */
export const ShowcaseEditor = ({
  caseColor, items, showcase, setShowcase, activeSlot, setActiveSlot,
  simple = false,
}) => {
  const boxRef = useRef(null);
  const rectRef = useRef(null);   // sürükleme başında bir kez ölçülür

  const patch = useCallback((i, next) => {
    setShowcase(prev => prev.map((s, k) => (k === i ? { ...s, ...next } : s)));
  }, [setShowcase]);

  const onPointerDown = useCallback((e) => {
    if (!boxRef.current) return;
    // ⚠️ Ölçü BİR KEZ: her harekette okumak reflow zorlar.
    rectRef.current = boxRef.current.getBoundingClientRect();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    move(e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlot]);

  const move = useCallback((e) => {
    const r = rectRef.current;
    if (!r || !r.width || !r.height) return;
    const x = clamp(((e.clientX - r.left) / r.width) * 100, -40, 140);
    const y = clamp(((e.clientY - r.top) / r.height) * 100, -40, 140);
    patch(activeSlot, { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
  }, [activeSlot, patch]);

  const onPointerMove = useCallback((e) => {
    // buttons === 0 → düğme basılı değil, sürükleme yok
    if (e.buttons === 0) return;
    move(e);
  }, [move]);

  const onPointerUp = useCallback((e) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    rectRef.current = null;
  }, []);

  const slot = showcase[activeSlot] || DEFAULT_SHOWCASE[activeSlot];
  const hasItem = (items?.length || 0) > activeSlot;

  return (
    <div className="bg-[#15171a] border border-[#2A2D31] rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest">
          {simple ? 'Drag · Resize' : 'Showcase Layout'}
        </span>
        <button
          type="button"
          onClick={() => setShowcase(DEFAULT_SHOWCASE)}
          className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          Reset
        </button>
      </div>

      {/* Sürüklenebilir önizleme */}
      <div
        ref={boxRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="relative h-44 rounded-lg bg-[#0f1113] border border-[#2A2D31] overflow-hidden cursor-crosshair touch-none select-none"
        title="Drag to position"
      >
        <DynamicCasePreview caseColor={caseColor} items={items} showcase={showcase} />

        {/* Aktif slot işareti — hangi eşyayı taşıdığını gösterir.
            pointer-events-none: sürüklemeyi engellemesin. */}
        {hasItem && (
          <div
            className="absolute pointer-events-none border-2 border-dashed border-[#B059E8] rounded-md"
            style={{
              left: `${slot.x}%`,
              top: `${slot.y}%`,
              width: slot.size,
              height: slot.size,
              transform: `translate(-50%, -50%) rotate(${slot.rotate}deg)`,
              zIndex: 30,
            }}
          />
        )}
      </div>

      {/* Slot seçici */}
      <div className="grid grid-cols-3 gap-2 mt-3">
        {SLOT_LABELS.map((label, i) => {
          const disabled = (items?.length || 0) <= i;
          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => setActiveSlot(i)}
              className={`py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors ${
                disabled
                  ? 'bg-[#0f1113] text-zinc-700 cursor-not-allowed'
                  : activeSlot === i
                    ? 'bg-[#B059E8] text-white'
                    : 'bg-[#1a1d21] text-zinc-400 hover:text-white'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* İnce ayar — sade modda YALNIZCA boyut. */}
      <div className="mt-3 space-y-2">
        <Slider label="Size" value={slot.size} min={16} max={160} step={1}
                onChange={(v) => patch(activeSlot, { size: v })} suffix="px" />
        {!simple && (
          <>
            <Slider label="Rotate" value={slot.rotate} min={-45} max={45} step={1}
                    onChange={(v) => patch(activeSlot, { rotate: v })} suffix="°" />
            <div className="grid grid-cols-2 gap-2">
              <Slider label="X" value={slot.x} min={-40} max={140} step={0.5}
                      onChange={(v) => patch(activeSlot, { x: v })} suffix="%" />
              <Slider label="Y" value={slot.y} min={-40} max={140} step={0.5}
                      onChange={(v) => patch(activeSlot, { y: v })} suffix="%" />
            </div>
          </>
        )}
      </div>

      {!hasItem && (
        <p className="text-[10px] text-zinc-600 mt-3 text-center">
          No item for this slot yet — add items to the case.
        </p>
      )}
    </div>
  );
};

const Slider = ({ label, value, min, max, step, onChange, suffix }) => (
  <label className="block">
    <span className="flex items-center justify-between text-[10px] text-zinc-500 font-bold uppercase tracking-wider mb-1">
      {label}
      <span className="text-zinc-400 mm-num normal-case">{value}{suffix}</span>
    </span>
    <input
      type="range"
      min={min} max={max} step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-[#B059E8] cursor-pointer"
    />
  </label>
);

export default ShowcaseEditor;